import { moveItemInArray, transferArrayItem, CdkDragDrop, CdkDragSortEvent } from '@angular/cdk/drag-drop';
import { Component, OnInit, Input, forwardRef, ContentChild, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { differenceWith } from 'lodash-es';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';

export const NIMBUS_DUALLISTBOX_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  /* eslint-disable @typescript-eslint/no-use-before-define */
  useExisting: forwardRef(() => NimbusDualListBoxComponent),
  multi: true,
};

@Component({
  standalone: false,
  selector: 'nimbus-dual-list-box',
  templateUrl: './nimbus-dual-list-box.component.html',
  providers: [NIMBUS_DUALLISTBOX_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusDualListBoxComponent<TItem = any> implements OnInit, ControlValueAccessor {
  @Input() public source: Array<TItem>;
  @Input() public idField: keyof TItem;
  @Input() public sourceName = 'Available Items';
  @Input() public selectedName = 'Selected Items';
  @Input() public tipMessage = 'Double click or drand and drop';
  @Input() public emptyMessage = 'Drag something here';

  @Input() public disabled: boolean;

  @ContentChild('itemTemplate', { static: true }) public itemTemplateRef: TemplateRef<{ item: TItem; index: number }>;

  protected onValueChanged: (val: Array<TItem[keyof TItem]>) => void;
  protected onTouched: () => void;

  public availableItems: Array<TItem> = [];
  public selectedItems: Array<TItem> = [];
  public value: Array<TItem[keyof TItem]>;
  public icons = ICONS;

  public ngOnInit(): void {
    if (!this.idField) {
      throw new Error('Please specify input "idField"');
    }
    if (!this.itemTemplateRef) {
      throw new Error('Please specify #itemTemplate');
    }
  }

  public writeValue(obj: Array<TItem[keyof TItem]>): void {
    if (obj instanceof Array) {
      this.availableItems = differenceWith(this.source, obj, (item, id) => item[this.idField] === id);
      this.selectedItems = obj.map((id) => this.source.find((el) => el[this.idField] === id));
    } else {
      throw new Error('Not supported structure');
    }

    this.value = obj;
    if (this.onValueChanged) {
      this.onValueChanged(obj);
    }
  }

  public addToList(event: CdkDragDrop<TItem[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }

    this.value = this.selectedItems.map((item) => item[this.idField]);
    if (this.onValueChanged) {
      this.onValueChanged(this.value);
    }
  }

  public sortList(event: CdkDragSortEvent<TItem[]>) {
    this.selectedItems = event.container.data;
    this.value = event.container.data.map((item) => item[this.idField]);
    if (this.onValueChanged) {
      this.onValueChanged(this.value);
    }
  }

  public registerOnChange(fn: (val: Array<TItem[keyof TItem]>) => void): void {
    this.onValueChanged = fn;
  }
  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public trackItem(index: number, item: TItem) {
    return item[this.idField];
  }

  public addItem(item: TItem) {
    const value = this.value ? this.value.concat(item[this.idField]) : [item[this.idField]];
    this.writeValue(value);
  }

  public removeItem(item: TItem) {
    this.writeValue(this.value.filter((v) => v !== item[this.idField]));
  }
}
